import { KnownApiError } from "@mirage-web/networking/known-api-error";
import { AbortError } from "@mirage-web/utils/abort-signals";
import { promiseWithResolvers } from "@mirage-web/utils/promise-with-resolvers";
import { withRetry } from "@mirage-web/utils/retry";
import { createStoreGetters } from "@mirage-web/utils/root-store";
import { createUseStoreWithSelectorDeferred } from "@mirage-web/utils/zustand-store";
import { decodeJwt } from "jose";
import { createStore } from "zustand";
import { combine } from "zustand/middleware";

import { resetOasisAnalyticsIdentity } from "~/lib/analytics/mixpanel";
import { setAppearanceUser } from "~/modules/appearance/store";

import { isLocalOasisDevAuthEnabled, loginCredentials } from "./credentials";
import {
  refreshAccessTokenMutation,
  logoutUserMutation,
  getUserQuery,
  UserProfileType,
} from "./network";

const TOKEN_EXPIRATION_BUFFER_SECONDS = 10; // 10 seconds

interface AuthStoreState {
  user: UserProfileType | null;

  refreshTokenPromise: Promise<string | null> | null;
}

export function createAuthStore() {
  return createStore(
    combine(
      {
        user: null,
        refreshTokenPromise: null,
      } as AuthStoreState,
      (set, get) => {
        const logout = async (
          reason: "refresh_access_token_failure" | "logout_clicked" | "account_deleted"
        ) => {
          if (reason === "logout_clicked") {
            await logoutUserMutation.trigger({ params: {} }).catch(console.error);
          }
          loginCredentials.remove();
          resetOasisAnalyticsIdentity();
          set({ user: null });
          setAppearanceUser(null);
        };

        const refreshAccessToken = async (force = false) => {
          const credentials = loginCredentials.get();
          if (!credentials) {
            return null;
          }

          if (get().refreshTokenPromise) {
            // if there is already a refresh token promise, return it
            return get().refreshTokenPromise;
          }

          const isTokenStillValid = !isTokenExpired(credentials.accessToken);

          if (!force && isTokenStillValid) {
            return credentials.accessToken;
          }

          const { promise, resolve } = promiseWithResolvers<string | null>();
          set({ refreshTokenPromise: promise });

          try {
            const authenticationResponse = await withRetry(
              () =>
                refreshAccessTokenMutation.trigger({
                  headers: {
                    Authorization: `Bearer ${credentials.refreshToken}`,
                  },
                  // we pass old access token to make sure we authorize against the same org
                  params: { oldAccessToken: credentials.accessToken },
                }),
              {
                shouldRetry(error) {
                  // Don't retry on KnownApiError, this means the API returned a real response
                  // but it was an error. this means the token is invalid and we should log the
                  // user out.
                  return !(error instanceof KnownApiError);
                },
              }
            );

            loginCredentials.set({
              accessToken: authenticationResponse.accessToken,
              refreshToken: authenticationResponse.refreshToken,
              userId: credentials.userId,
            });

            resolve(authenticationResponse.accessToken);
            set({ refreshTokenPromise: null });

            return authenticationResponse.accessToken;
          } catch (_error) {
            logout("refresh_access_token_failure");
            window.location.href = "/";

            // we do not actually reject the stored promise here for the other request
            // since we are redirect the user to the login page anyway.

            // we throw an error so that the request aborts while the browser is redirecting
            throw new AbortError();
          }
        };

        const saveProfileResponse = async (response: UserProfileType) => {
          setAppearanceUser(response.userId);
          set({ user: response });
        };

        async function refreshProfile() {
          const credentials = loginCredentials.get();
          if (!credentials && !isLocalOasisDevAuthEnabled()) {
            return null;
          }

          const profileResponse = await getUserQuery.trigger({});
          await saveProfileResponse(profileResponse);

          return { user: profileResponse };
        }

        return {
          async init() {},
          async ensureProfile() {
            const { user } = get();

            if (user) {
              return { user, wasPreviouslyLoggedIn: true };
            }

            const credentials = loginCredentials.get();
            if (!credentials && !isLocalOasisDevAuthEnabled()) {
              return null;
            }

            const profileResponse = await getUserQuery.trigger({});

            await saveProfileResponse(profileResponse);

            return {
              user: profileResponse,
              wasPreviouslyLoggedIn: false,
            };
          },
          refreshProfile,
          getIsLikelyLoggedIn() {
            // NOTE: we actually do not want to require access token freshness here
            // because even if the token is expired, we can likely refresh it using
            // the refresh token.
            // if the refresh token is also expired, we will not be able to refresh it
            // and the user will get logged out.
            return !!loginCredentials.get() || isLocalOasisDevAuthEnabled();
          },
          getAccessToken() {
            return refreshAccessToken();
          },
          getNeedsOrganizationRecovery() {
            const credentials = loginCredentials.get();
            if (!credentials) {
              return false;
            }

            try {
              const organizationId = decodeJwt(credentials.accessToken).org_id;
              return typeof organizationId !== "string" || organizationId.length === 0;
            } catch {
              return false;
            }
          },
          async recoverOrganizationSession() {
            const accessToken = await refreshAccessToken(true);
            if (!accessToken) {
              return null;
            }

            return refreshProfile();
          },
          saveLoginCredentials(credentials: {
            accessToken: string;
            refreshToken: string;
            userId: string;
          }) {
            loginCredentials.set(credentials);
          },
          logout,
        };
      }
    )
  );
}

type AuthStore = ReturnType<ReturnType<typeof createAuthStore>["getState"]>;

export const {
  getStore: getAuthStore,
  getStoreAsync: getAuthStoreAsync,
  getStoreApi: getAuthStoreApi,
} = createStoreGetters<AuthStore>("auth");

export const useAuthStore = createUseStoreWithSelectorDeferred(() => getAuthStoreApi());

function isTokenExpired(token: string) {
  try {
    const decodedToken = decodeJwt(token);
    if (!decodedToken.exp) {
      return true;
    }

    if (typeof decodedToken.exp !== "number") {
      return true;
    }

    const now = Math.floor(Date.now() / 1000);

    return now >= decodedToken.exp - TOKEN_EXPIRATION_BUFFER_SECONDS;
  } catch (_error) {
    return true;
  }
}
